/**
 * Public page / Site Content helpers (white-label ready).
 *
 * Prefer configData.site_content from the API over hardcoded genre IDs / header links.
 */

import configuration from "react-global-configuration";
import { apiConstants } from "../Constant/constants";

const FALLBACK_PUBLIC_PAGES = [
  {
    key: "series",
    slug: "series",
    path: "/series",
    page_type: "SERIES",
    legacy_genre_id: apiConstants.SERIES,
    label: "Series",
    label_mobile: "Series",
    nav_desktop: true,
    nav_mobile: true,
    enabled: true,
  },
  {
    key: "movies",
    slug: "movies",
    path: "/movies",
    page_type: "FLIMS",
    legacy_genre_id: apiConstants.MOVIES,
    label: "Movies",
    label_mobile: "Movies",
    nav_desktop: true,
    nav_mobile: true,
    enabled: true,
  },
  {
    key: "kids",
    slug: "kids",
    path: "/kids",
    page_type: "KIDS",
    legacy_genre_id: apiConstants.KIDS,
    label: "Kids",
    label_mobile: "Kids",
    nav_desktop: true,
    nav_mobile: true,
    enabled: true,
  },
  {
    key: "shop",
    slug: "shop",
    path: "/shop",
    page_type: "SHOP",
    legacy_genre_id: 0,
    label: "Shop",
    label_mobile: "Shop",
    nav_desktop: true,
    nav_mobile: true,
    enabled: true,
  },
];

const FALLBACK_HEADER_ACTIONS = [
  {
    key: "referral",
    label: "Refer",
    icon: "fa-user-plus",
    enabled: true,
    destination_type: "internal",
    internal_path: "/referfriends",
    external_url: "",
    href: "/referfriends",
    is_external: false,
    open_in_new_tab: false,
    sort_order: 10,
  },
  {
    key: "donation",
    label: "Donate",
    icon: "fa-hands-helping",
    enabled: false,
    destination_type: "internal",
    internal_path: "/donate",
    external_url: "",
    href: "/donate",
    is_external: false,
    open_in_new_tab: false,
    sort_order: 20,
  },
];

export function getSiteContent() {
  try {
    return configuration.get("configData.site_content") || null;
  } catch (e) {
    return null;
  }
}

export function getPublicPages() {
  const site = getSiteContent();
  let pages;
  if (site && Array.isArray(site.public_pages) && site.public_pages.length) {
    pages = site.public_pages.map((page) => {
      // Pre-A-006.1B APIs mislabeled Kids as Shop (page_type KIDS). Remap those only.
      const isLegacyShopKids =
        (page.key === "shop" ||
          String(page.slug || "").toLowerCase() === "shop") &&
        String(page.page_type || "").toUpperCase() === "KIDS";
      const key = isLegacyShopKids ? "kids" : page.key;
      const slug = isLegacyShopKids
        ? "kids"
        : key === "shop"
          ? "shop"
          : page.slug;
      const label =
        isLegacyShopKids && (!page.label || page.label === "Shop")
          ? "Kids"
          : page.label;

      return {
        ...page,
        key,
        slug,
        label,
        path: `/${slug}`,
        label_mobile: page.label_mobile || label,
        page_type: isLegacyShopKids ? "KIDS" : page.page_type,
      };
    });
  } else {
    pages = FALLBACK_PUBLIC_PAGES.filter((page) => {
      if (page.key === "kids" && !isKidsSectionEnabled()) {
        return false;
      }
      if (page.key === "shop" && !isShopSectionEnabled()) {
        return false;
      }
      return true;
    });
  }

  // Categories-like visibility: omit disabled sections (and any enabled:false pages).
  return pages.filter((page) => {
    if (page.enabled === false) {
      return false;
    }
    if (
      (page.key === "kids" || page.page_type === "KIDS") &&
      !isKidsSectionEnabled()
    ) {
      return false;
    }
    if (
      (page.key === "shop" || page.page_type === "SHOP") &&
      !isShopSectionEnabled()
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Master Kids visibility toggle (mirrors Categories is_visible at section level).
 * Prefers site_content.kids_section.enabled; falls back to flat settings key.
 * Defaults to enabled when the setting is missing (backward compatible).
 */
export function isKidsSectionEnabled() {
  try {
    const site = getSiteContent();
    if (
      site &&
      site.kids_section &&
      typeof site.kids_section.enabled !== "undefined"
    ) {
      return !!site.kids_section.enabled;
    }
    const value = configuration.get("configData.is_kids_section_enabled");
    if (value === undefined || value === null || value === "") {
      return true;
    }
    return value == 1 || value === true || value === "1";
  } catch (e) {
    return true;
  }
}

/**
 * Master Shop visibility toggle (same pattern as Kids).
 * Prefers site_content.shop_section.enabled; falls back to flat settings key.
 * Defaults to enabled when the setting is missing (backward compatible).
 */
export function isShopSectionEnabled() {
  try {
    const site = getSiteContent();
    if (
      site &&
      site.shop_section &&
      typeof site.shop_section.enabled !== "undefined"
    ) {
      return !!site.shop_section.enabled;
    }
    const value = configuration.get("configData.is_shop_section_enabled");
    if (value === undefined || value === null || value === "") {
      return true;
    }
    return value == 1 || value === true || value === "1";
  } catch (e) {
    return true;
  }
}

/**
 * Coming Soon / future commerce landing copy + hero from Site Content.
 */
export function getShopPageConfig() {
  const site = getSiteContent();
  const siteName = configuration.get("configData.site_name") || "Shop";
  const defaults = {
    title: "Shop",
    subtitle: "Coming Soon",
    description: `The ${siteName} Shop is currently under development. Soon you'll be able to browse books, digital resources, music, films, and merchandise from ${siteName} and participating Organizations.`,
    hero_title: "Shop",
    hero_subtitle: "Coming Soon",
    hero_description: `The ${siteName} Shop is currently under development. Soon you'll be able to browse books, digital resources, music, films, and merchandise from ${siteName} and participating Organizations.`,
    hero_image: "",
    hero_image_mobile: "",
    hero_video: "",
    hero_overlay_opacity: 0.55,
    hero_background_alignment: "center",
    hero_image_enabled: true,
    hero_image_mobile_enabled: true,
    hero_video_enabled: true,
    hero_video_play_on: {
      desktop: true,
      tablet: true,
      mobile: true,
    },
    hero_image_display_duration: 3,
    hero_transition_style: "fade",
    hero_video_autoplay: true,
    hero_video_loop: true,
    hero_video_muted: true,
    hero_video_playsinline: true,
    primary_cta_label: "Browse Home",
    primary_cta_url: "/home",
    secondary_cta_label: "",
    secondary_cta_url: "",
    seo_title: "",
    seo_description: "",
  };

  if (site && site.shop_page) {
    const merged = { ...defaults, ...site.shop_page };
    // Empty string is intentional (admin cleared the field). Use nullish coalescing
    // only so "" is preserved while missing keys still get defaults from the spread.
    const firstDefined = (...values) => {
      for (let i = 0; i < values.length; i++) {
        if (values[i] !== undefined && values[i] !== null) {
          return values[i];
        }
      }
      return undefined;
    };
    merged.hero_title = firstDefined(
      site.shop_page.hero_title,
      site.shop_page.title,
      defaults.hero_title
    );
    merged.hero_subtitle = firstDefined(
      site.shop_page.hero_subtitle,
      site.shop_page.subtitle,
      defaults.hero_subtitle
    );
    merged.hero_description = firstDefined(
      site.shop_page.hero_description,
      site.shop_page.description,
      defaults.hero_description
    );
    merged.title = firstDefined(
      site.shop_page.title,
      site.shop_page.hero_title,
      defaults.title
    );
    merged.subtitle = firstDefined(
      site.shop_page.subtitle,
      site.shop_page.hero_subtitle,
      defaults.subtitle
    );
    merged.description = firstDefined(
      site.shop_page.description,
      site.shop_page.hero_description,
      defaults.description
    );
    merged.primary_cta_label = firstDefined(
      site.shop_page.primary_cta_label,
      defaults.primary_cta_label
    );
    merged.primary_cta_url = firstDefined(
      site.shop_page.primary_cta_url,
      defaults.primary_cta_url
    );
    merged.secondary_cta_label = firstDefined(
      site.shop_page.secondary_cta_label,
      defaults.secondary_cta_label
    );
    merged.secondary_cta_url = firstDefined(
      site.shop_page.secondary_cta_url,
      defaults.secondary_cta_url
    );
    merged.seo_title = firstDefined(site.shop_page.seo_title, defaults.seo_title);
    merged.seo_description = firstDefined(
      site.shop_page.seo_description,
      defaults.seo_description
    );
    const opacity = parseFloat(merged.hero_overlay_opacity);
    merged.hero_overlay_opacity = Number.isFinite(opacity)
      ? Math.max(0, Math.min(1, opacity > 1 ? opacity / 100 : opacity))
      : defaults.hero_overlay_opacity;

    // A-006.1D — normalize experience fields (missing keys → defaults for BC).
    const asBool = (value, fallback) => {
      if (value === undefined || value === null || value === "") return fallback;
      return value === true || value === 1 || value === "1";
    };
    merged.hero_image_enabled = asBool(
      merged.hero_image_enabled,
      defaults.hero_image_enabled
    );
    merged.hero_image_mobile_enabled = asBool(
      merged.hero_image_mobile_enabled,
      defaults.hero_image_mobile_enabled
    );
    merged.hero_video_enabled = asBool(
      merged.hero_video_enabled,
      defaults.hero_video_enabled
    );
    const playOn = merged.hero_video_play_on || {};
    merged.hero_video_play_on = {
      desktop: asBool(playOn.desktop, defaults.hero_video_play_on.desktop),
      tablet: asBool(playOn.tablet, defaults.hero_video_play_on.tablet),
      mobile: asBool(playOn.mobile, defaults.hero_video_play_on.mobile),
    };
    const duration = parseInt(merged.hero_image_display_duration, 10);
    merged.hero_image_display_duration = Number.isFinite(duration)
      ? Math.max(0, Math.min(30, duration))
      : defaults.hero_image_display_duration;
    const style = String(merged.hero_transition_style || "").toLowerCase();
    merged.hero_transition_style = ["fade", "cut", "fade_zoom"].includes(style)
      ? style
      : defaults.hero_transition_style;
    merged.hero_video_autoplay = asBool(
      merged.hero_video_autoplay,
      defaults.hero_video_autoplay
    );
    merged.hero_video_loop = asBool(
      merged.hero_video_loop,
      defaults.hero_video_loop
    );
    merged.hero_video_muted = asBool(
      merged.hero_video_muted,
      defaults.hero_video_muted
    );
    merged.hero_video_playsinline = asBool(
      merged.hero_video_playsinline,
      defaults.hero_video_playsinline
    );

    return merged;
  }

  return defaults;
}

export function getPublicPageBySlug(slug) {
  if (!slug) return null;
  const normalized = String(slug).replace(/^\//, "").toLowerCase();
  return (
    getPublicPages().find(
      (page) =>
        String(page.slug).toLowerCase() === normalized ||
        String(page.key).toLowerCase() === normalized
    ) || null
  );
}

export function getPublicPageByLegacyId(id) {
  const numeric = parseInt(id, 10);
  if (Number.isNaN(numeric)) return null;
  return (
    getPublicPages().find(
      (page) => parseInt(page.legacy_genre_id, 10) === numeric
    ) || null
  );
}

/**
 * Resolve API page_type for Genres from slug route or legacy /genre/:id.
 */
export function resolvePublicPageType(match) {
  if (!match) {
    return { page_type: "FLIMS", page: null };
  }

  const paramId = match.params && match.params.id;
  const paramSlug =
    (match.params && match.params.slug) ||
    (match.params && match.params.publicSlug);

  if (paramSlug) {
    const bySlug = getPublicPageBySlug(paramSlug);
    if (bySlug) {
      return { page_type: bySlug.page_type, page: bySlug };
    }
  }

  const path = (match.path || match.url || "").replace(/\/$/, "");
  const pathSlug = path.split("/").filter(Boolean).pop();
  if (pathSlug && !paramId) {
    const byPath = getPublicPageBySlug(pathSlug);
    if (byPath) {
      return { page_type: byPath.page_type, page: byPath };
    }
  }

  if (paramId !== undefined && paramId !== null && paramId !== "") {
    if (!/^\d+$/.test(String(paramId))) {
      const bySlugParam = getPublicPageBySlug(paramId);
      if (bySlugParam) {
        return { page_type: bySlugParam.page_type, page: bySlugParam };
      }
    }
    const byLegacy = getPublicPageByLegacyId(paramId);
    if (byLegacy) {
      return { page_type: byLegacy.page_type, page: byLegacy };
    }
  }

  // Prefer path URL when Route match.path is a pattern without a resolvable slug
  // (avoids intermittent FLIMS/Movies default while Series/Kids are intended).
  const urlPath = (match.url || "").replace(/\/$/, "");
  const urlSlug = urlPath.split("/").filter(Boolean).pop();
  if (urlSlug && !paramId) {
    const byUrl = getPublicPageBySlug(urlSlug);
    if (byUrl) {
      return { page_type: byUrl.page_type, page: byUrl };
    }
  }

  return { page_type: "FLIMS", page: null };
}

export function getDonationPageConfig() {
  const site = getSiteContent();
  const siteName = configuration.get("configData.site_name") || "Pentecost+";

  const defaults = {
    enabled: configuration.get("configData.is_donate_system_enabled") == 1,
    title: "Donate Now",
    subtitle: "",
    body:
      "Your support helps us keep creating and sharing meaningful content. Thank you for giving today.",
    image: "",
    cta_label: "Pay Now",
    amounts: ["10", "20", "50", "80", "100"],
    currency_symbol: "$",
    legal_text: `${siteName} is committed to your privacy. Your payment details will be processed by the selected payment provider, and a record of your donation will be stored by ${siteName}.`,
    privacy_link_label: "Privacy Policy",
    privacy_link_url: "/page/privacy",
    custom_links: [],
  };

  if (site && site.donation_page) {
    return { ...defaults, ...site.donation_page };
  }

  return defaults;
}

export function getReferralPageConfig() {
  const site = getSiteContent();
  const siteName = configuration.get("configData.site_name") || "Pentecost+";

  const defaults = {
    enabled: true,
    hero_title: `Tell friends about ${siteName}`,
    hero_subtitle: "",
    hero_description: "",
    rich_content: "",
    hero_image: "",
    primary_cta_label: "Copy Link",
    secondary_cta_label: "",
    secondary_cta_url: "",
    instructions: "",
    privacy_link_label: "Privacy Policy",
    privacy_link_url: "/page/privacy",
    terms_link_label: "Terms of Use",
    terms_link_url: "/page/terms",
    referral_terms_link_label: "Referral Program Terms",
    referral_terms_link_url: "",
    custom_link_1_label: "",
    custom_link_1_url: "",
    custom_link_2_label: "",
    custom_link_2_url: "",
  };

  if (site && site.referral_page) {
    return { ...defaults, ...site.referral_page };
  }

  return defaults;
}

/**
 * Billing Details page CMS (side-panel image). Empty image → legacy static asset.
 */
export function getBillingDetailsConfig() {
  const site = getSiteContent();
  const defaults = {
    image: "",
  };

  if (site && site.billing_details_page) {
    return { ...defaults, ...site.billing_details_page };
  }

  return defaults;
}

/**
 * Resolved Billing Details left-panel image URL.
 * Prefers admin upload from site_content; falls back to legacy /assets/img/billing.jpg.
 */
export function resolveBillingDetailsImageUrl() {
  const page = getBillingDetailsConfig();
  const uploaded = page && page.image ? String(page.image).trim() : "";
  if (uploaded) {
    return uploaded;
  }
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin + "/assets/img/billing.jpg";
  }
  return "/assets/img/billing.jpg";
}

/**
 * Configuration-driven header actions (referral, donation, future icons).
 */
export function getHeaderActions() {
  const site = getSiteContent();
  let actions =
    site && site.header && Array.isArray(site.header.actions)
      ? site.header.actions
      : null;

  if (!actions || !actions.length) {
    actions = FALLBACK_HEADER_ACTIONS.map((action) => {
      if (action.key === "donation") {
        return {
          ...action,
          enabled: configuration.get("configData.is_donate_system_enabled") == 1,
        };
      }
      if (action.key === "referral") {
        const flat = configuration.get("configData.is_referral_system_enabled");
        const enabled =
          flat === undefined || flat === null || flat === ""
            ? true
            : flat == 1 || flat === true || flat === "1";
        return { ...action, enabled };
      }
      return action;
    });
  }

  return [...actions]
    .filter((action) => action && action.enabled)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

export function isReferralHeaderEnabled() {
  return getHeaderActions().some((action) => action.key === "referral");
}

export function isDonateHeaderEnabled() {
  return getHeaderActions().some((action) => action.key === "donation");
}

/**
 * Master Cast header visibility (mirrors donation/referral toggles).
 * Prefers site_content.header.is_cast_system_enabled; falls back to flat key.
 * Defaults to enabled when the setting is missing (Pentecost+ default ON).
 */
export function isCastHeaderEnabled() {
  try {
    const site = getSiteContent();
    if (
      site &&
      site.header &&
      typeof site.header.is_cast_system_enabled !== "undefined"
    ) {
      return !!site.header.is_cast_system_enabled;
    }
    const value = configuration.get("configData.is_cast_system_enabled");
    if (value === undefined || value === null || value === "") {
      return true;
    }
    return value == 1 || value === true || value === "1";
  } catch (e) {
    return true;
  }
}

export { isExternalHref, normalizeExternalUrl, formatExternalUrlForDisplay } from "./urls";

